import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { SubmitTicketDraft, setSubmitTicketQuery } from "../../../store/slices/ticketingPortalSlice";
import {
  fetchSubmitTicketFormConfigAndFields,
} from "../../../store/thunks/submitTicketThunk";
import {
  selectAccessibleModuleNames,
} from "../../../store/selectors/appDataSelectors";

type SubmitLocationRow = {
  locName: string;
  locId: string;
  locSapId: string;
  region: string;
  market: string;
};

/**
 * Resolves the current value of a dynamic field. Some fields are stored on the draft
 * (people/loads impacted, submitter, stakeholders) rather than in triggerValues.
 */
export const getDynamicFieldValue = (
  fieldName: string,
  draft: Pick<SubmitTicketDraft, "impactedPeople" | "loadImpact" | "submitter" | "stakeholders">,
  triggerValues: Record<string, string>
): string => {
  if (fieldName === "number_of_people_impacted") {
    return draft.impactedPeople || "";
  }
  if (fieldName === "percent_of_loads_impacted") {
    return draft.loadImpact || "";
  }
  if (fieldName === "submitter") {
    return draft.submitter || "";
  }
  if (fieldName === "secondary_stakeholders") {
    return draft.stakeholders || "";
  }
  return triggerValues[fieldName] || "";
};

const useSubmitTicketData = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    query,
    relatedIssues,
    loading,
    error,
    formCategories,
    subCategoryMap,
    categoryConfig,
    dynamicFields,
    triggerValues,
    triggerFieldOptions,
    draft,
    formConfigLoading,
    formConfigError,
    allLocations,
    fallbackLocations,
    userRole,
  } = useSelector((state: RootState) => ({
    query: state.ticketingPortal.submitTicketQuery,
    relatedIssues: state.ticketingPortal.submitTicketRelatedIssues,
    loading: state.ticketingPortal.submitTicketLoading,
    error: state.ticketingPortal.submitTicketError,
    formCategories: state.ticketingPortal.submitTicketCategories,
    subCategoryMap: state.ticketingPortal.submitTicketSubCategoryMap,
    categoryConfig: state.ticketingPortal.submitTicketCategoryConfig,
    dynamicFields: state.ticketingPortal.submitTicketDynamicFields,
    triggerValues: state.ticketingPortal.submitTicketTriggerValues,
    triggerFieldOptions: state.ticketingPortal.submitTicketTriggerFieldOptions,
    draft: state.ticketingPortal.submitTicketDraft,
    formConfigLoading: state.ticketingPortal.submitTicketFormConfigLoading,
    formConfigError: state.ticketingPortal.submitTicketFormConfigError,
    allLocations: state.ticketingPortal.allLocations,
    fallbackLocations: state.appData.locations ?? [],
    userRole: state.appData.userInfo?.roleName,
  }));
  // triggerFieldOptions kept in selector for pass-through to panel (static options from /fields response)

  const submitLocationRows = useMemo<SubmitLocationRow[]>(() => {
    if (allLocations.length > 0) {
      return allLocations
        .map((location) => ({
          locName: String(location?.locName ?? "").trim(),
          locId: String(location?.locId ?? "").trim(),
          locSapId: String(location?.locSapId ?? "").trim(),
          region: String(location?.region ?? "").trim(),
          market: String(location?.market ?? "").trim(),
        }))
        .filter((location) => Boolean(location.locName));
    }

    return fallbackLocations
      .map((location) => ({
        locName: String(location?.name ?? "").trim(),
        locId: String(location?.locationId ?? location?.id ?? "").trim(),
        locSapId: String(location?.locSapId ?? "").trim(),
        region: String(location?.region ?? "").trim(),
        market: String(location?.market ?? "").trim(),
      }))
      .filter((location) => Boolean(location.locName));
  }, [allLocations, fallbackLocations]);

  const locationNames = useMemo(
    () => [...new Set(submitLocationRows.map((l) => l.locName).filter(Boolean))].sort(),
    [submitLocationRows]
  );
  const locationsWithSapId = locationNames;
  const regionOptions = useMemo(
    () => {
      const regionSet = new Set(submitLocationRows.map((l) => l.region).filter(Boolean));
      
      // Add "COE" option for authorized users (COE or Super User roles)
      if (userRole === "COE" || userRole === "Super User") {
        regionSet.add("COE");
      }
      
      return [...regionSet].sort();
    },
    [submitLocationRows, userRole]
  );
  const marketOptions = useMemo(
    () => [...new Set(submitLocationRows.map((l) => l.market).filter(Boolean))].sort(),
    [submitLocationRows]
  );
  const moduleOptions = (useSelector(selectAccessibleModuleNames) as string[]) || [];
  const locationSapIdByName = useMemo(() => {
    const sapByLocation: Record<string, string> = {};
    submitLocationRows.forEach((location) => {
      if (!(location.locName in sapByLocation) || (!sapByLocation[location.locName] && location.locSapId)) {
        sapByLocation[location.locName] = location.locSapId;
      }
    });
    return sapByLocation;
  }, [submitLocationRows]);

  const locationDisplayByName = useMemo(() => {
    const labels: Record<string, string> = {};
    submitLocationRows.forEach((location) => {
      if (!(location.locName in labels)) {
        labels[location.locName] = location.locId
          ? `${location.locName} (${location.locId})`
          : location.locName;
      }
    });
    return labels;
  }, [submitLocationRows]);

  const locationSearchMetadata = useMemo(() => {
    const metadata: Record<string, { locationId: string; sapId: string }> = {};
    submitLocationRows.forEach((location) => {
      if (!(location.locName in metadata)) {
        metadata[location.locName] = {
          locationId: location.locId,
          sapId: location.locSapId,
        };
      }
    });
    return metadata;
  }, [submitLocationRows]);

  useEffect(() => {
    // GAP-09: duplicate check is now triggered from SubmitTicketScreen on subCategory change
    // The query-based related issues search panel is removed in favour of auto-triggered duplicate modal
  }, [dispatch, query]);

  // Category tree depends on the user's location and role (beta rollout) - refetch when either changes.
  const betaLocationName = useSelector((state: RootState) => state.appData.selectedLocation?.name);
  const betaRoleId = useSelector((state: RootState) => state.appData.userInfo?.roleId);

  useEffect(() => {
    // NEW-B: single combined call replaces former two separate calls
    dispatch(fetchSubmitTicketFormConfigAndFields());
  }, [dispatch, betaLocationName, betaRoleId]);

  const activeTriggerFields = useMemo(() => {
    const selectedCategory = categoryConfig.find((item) => item.name === draft.category);
    const selectedCategoryId = selectedCategory?.id ?? null;
    const selectedSubCategory = selectedCategory?.subCategories.find((item) => item.name === draft.subCategory);
    const selectedSubCategoryId = selectedSubCategory?.id ?? null;
    const selectedTertiaryId =
      selectedSubCategory?.tertiaryCategories?.find((item) => item.name === draft.tertiaryCategory)?.id ?? null;

    return dynamicFields.filter((field) => {
      if (!field.triggers || field.triggers.length === 0) {
        return false;
      }

      return field.triggers.some((trigger) => {
        const categoryMatch = trigger.categoryId === null || trigger.categoryId === selectedCategoryId;
        const subCategoryMatch = trigger.subCategoryId === null || trigger.subCategoryId === selectedSubCategoryId;
        const tertiaryMatch = trigger.tertiaryId == null || trigger.tertiaryId === selectedTertiaryId;
        const configuredDependsOn = trigger.dependsOnFieldName;
        const configuredDependsOnValue = trigger.dependsOnFieldValue;
        const configuredDependsOnValues = trigger.dependsOnFieldValues;

        let dependencyMatch = true;
        if (configuredDependsOn) {
          const actualValue = getDynamicFieldValue(configuredDependsOn, draft, triggerValues);
          const expectedValues = configuredDependsOnValues && configuredDependsOnValues.length
            ? configuredDependsOnValues
            : configuredDependsOnValue
            ? [configuredDependsOnValue]
            : [];

          dependencyMatch = expectedValues.length
            ? expectedValues.includes(actualValue)
            : Boolean(actualValue);
        }

        if (field.fieldName === "workflow") {
          dependencyMatch = dependencyMatch && triggerValues.hud_gui === "HUD";
        }

        if (field.fieldName === "screen") {
          dependencyMatch = dependencyMatch && triggerValues.hud_gui === "GUI";
        }

        return categoryMatch && subCategoryMatch && tertiaryMatch && dependencyMatch;
      });
    });
  }, [
    categoryConfig,
    draft.category,
    draft.subCategory,
    draft.tertiaryCategory,
    draft.impactedPeople,
    draft.loadImpact,
    draft.submitter,
    draft.stakeholders,
    dynamicFields,
    triggerValues,
  ]);

  const tertiaryCategoryOptions = useMemo(() => {
    const selectedCategory = categoryConfig.find((item) => item.name === draft.category);
    const selectedSubCategory = selectedCategory?.subCategories.find((item) => item.name === draft.subCategory);
    return (selectedSubCategory?.tertiaryCategories ?? [])
      .filter((tertiary) => tertiary.isActive !== false)
      .map((tertiary) => tertiary.name);
  }, [categoryConfig, draft.category, draft.subCategory]);


  const updateQuery = (value: string) => {
    dispatch(setSubmitTicketQuery(value));
  };

  return {
    query,
    relatedIssues,
    loading,
    error,
    formCategories,
    subCategoryMap,
    categoryConfig,
    activeTriggerFields,
    tertiaryCategoryOptions,
    triggerValues,
    triggerFieldOptions,
    locationNames,
    locationsWithSapId,
    regionOptions,
    marketOptions,
    moduleOptions,
    locationSapIdByName,
    locationDisplayByName,
    locationSearchMetadata,
    submitLocationRows,
    formConfigLoading,
    formConfigError,
    updateQuery,
    userRole,
  };
};

export default useSubmitTicketData;
